import { useEffect, useState, useRef } from "react";
import { matchPath } from "react-router-dom";

import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAuthData, useOrganizationData } from "@/store/hooks";
import {
  getMyOrganization,
  getOrganizationBySlug,
} from "@/api/organization/thunks";
import {
  setOrganization,
  FALLBACK_ORGANIZATION,
} from "@/store/slices/organizationSlice";
import { getReservationTypeMappings } from "@/api/reservationTypeMappings/thunks";
import SplashLoader from "@/components/ui/splashLoader";
import { PUBLIC_PATHS } from "@/router/publicRoutes";
import { MAINSAIL_ADMIN_SLUG } from "@/utils/constants";

interface OrganizationProviderProps {
  children: React.ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAuthData();
  const { loading, organization } = useOrganizationData();
  const [hasInitialized, setHasInitialized] = useState(false);
  const mappingsFetchedRef = useRef(false);

  useEffect(() => {
    // Prevent infinite redirects and skip validation for certain pages.
    // Docusign callback pages render inside an iframe with no need for org
    // context — skip the slug fetch so we don't 404 on the env subdomain.
    const path = window.location.pathname;
    if (
      path === PUBLIC_PATHS.NOT_FOUND ||
      path === PUBLIC_PATHS.LOGIN ||
      path === PUBLIC_PATHS.DOCUMENT_SIGNING_REDIRECT ||
      matchPath(PUBLIC_PATHS.PROPERTY_DOCUSIGN_CALLBACK, path)
    ) {
      setHasInitialized(true);
      return;
    }

    setHasInitialized(false);

    // Global admins receive password-setup links at the ROOT origin (no org
    // subdomain), so the hostname slug won't resolve to an organization. The
    // token encodes the account, so the page needs no org context — render it
    // instead of redirecting to /not-found. On org subdomains the slug still
    // resolves normally, so org-subdomain behavior is unchanged.
    const isPasswordSetup = path === PUBLIC_PATHS.PASSWORD_SETUP;

    const fetchOrganization = async () => {
      try {
        const hostname = window.location.hostname;
        const currentSlug = hostname.split(".")[0];

        // Global (MainSail) admins authenticate through the reserved `mainsail`
        // subdomain, which the backend intentionally leaves org-less. Its slug
        // won't resolve to an organization, so treat it like the password-setup
        // page: render the global-admin auth screens (login, MFA,
        // forgot-password) instead of redirecting to /not-found.
        const isGlobalAdminContext = currentSlug === MAINSAIL_ADMIN_SLUG;

        // Fetch reservation type mappings for authenticated users (only once per session)
        // Await to ensure mappings are available before rendering children (prevents grey blocks on tape chart)
        if (user?.id && !mappingsFetchedRef.current) {
          mappingsFetchedRef.current = true;
          try {
            const data = await dispatch(getReservationTypeMappings()).unwrap();
            console.log("[DEBUG] Mappings loaded:", data?.length, "items");
          } catch (err) {
            console.error("[DEBUG] Failed to load mappings:", err);
          }
        }

        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          dispatch(setOrganization(FALLBACK_ORGANIZATION));
          return;
        }
        if (user?.id) {
          const result = await dispatch(getMyOrganization());

          if (getMyOrganization.rejected.match(result)) {
            dispatch(setOrganization(FALLBACK_ORGANIZATION));
          } else if (getMyOrganization.fulfilled.match(result)) {
            const userOrganization = result.payload;

            if (
              userOrganization.slug &&
              userOrganization.slug !== currentSlug
            ) {
              // Check if the current slug organization exists
              const slugOrgResult = await dispatch(
                getOrganizationBySlug({ slug: currentSlug })
              );

              if (getOrganizationBySlug.fulfilled.match(slugOrgResult)) {
                window.location.href = PUBLIC_PATHS.LOGIN;
                return;
              } else if (!isPasswordSetup && !isGlobalAdminContext) {
                window.location.href = PUBLIC_PATHS.NOT_FOUND;
                return;
              }
            }
          }
        } else {
          const result = await dispatch(
            getOrganizationBySlug({ slug: currentSlug })
          );

          if (
            getOrganizationBySlug.rejected.match(result) &&
            !isPasswordSetup &&
            !isGlobalAdminContext
          ) {
            window.location.href = PUBLIC_PATHS.NOT_FOUND;
            return;
          } else {
            //Organization exists - show login for that org"
          }
        }
      } catch (error) {
        throw Error(`Error fetching organization ${error}`);
      } finally {
        setHasInitialized(true);
      }
    };

    fetchOrganization();
  }, [dispatch, user?.id]);

  // Update document title
  useEffect(() => {
    if (organization?.name) {
      document.title = organization.name;
    }
  }, [organization?.name, user]);

  if (loading || !hasInitialized) {
    return <SplashLoader />;
  }

  return <>{children}</>;
};
